// Pieces shared by the three server-paged report tables (Details, Monthly, Daily): the filter bar with sort / export / delete, and the rows area
// of an open store (loading, error, empty, the expanded hierarchy). The cards of each table stay in its own file.
import { Fragment, useContext } from "react";
import "./PagedReport.css";
import { Context } from "../../context/Context";
import { translations } from "../../utils/lang";
import { formatMonth } from "../../utils/reportMonths";
import { DeleteModal } from "../modals/Delete";
import MonthRangeFilter from "./MonthRangeFilter";
import { REPORT_LABEL_INDEX } from "./reportViews";
import { Button, CloudDownload, NoticeBox, Pagination, SearchCheckList, Select, Trash2 } from "../ui";

// prefix: "details" | "monthly" | "daily" (CSS classes of the table). titles: the table's `inputTitles` (the period, the checkbox fields in order, "clear").
// fields: [{ key: "app"|"store"|"branch"|"status"|"cause", filterKey, onChange, wide? }].
export const PagedPanel = ({ report, prefix, titles, fields }) => {
  const { lang } = useContext(Context);
  const { text, filters, deleteState } = report;
  const totalCount = report.totals ? report.totals.count : 0;
  const sortOptions = ["dateDesc", "dateAsc", "amountDesc", "amountAsc"].map((value) => ({ value, label: text.sortOptions[value] }));
  return (
    <>
      <div className={`report-${prefix}-search-panel`}>
        <MonthRangeFilter lang={lang} range={report.range} onApply={report.applyRange} onReset={report.resetRange} loading={report.summary.loading} />
        <div className={`report-${prefix}-text-filter`}>
          {fields.map((field, i) => (
            <div key={field.key} className={field.wide ? "wide" : ""}>
              <SearchCheckList
                fullWidth="full-width"
                placeholder={titles[i + 1]}
                options={report.choices[field.key]}
                selected={filters[field.filterKey]}
                onChange={field.onChange}
              />
            </div>
          ))}
        </div>

        <div className="report-filter-actions">
          <Button variant="warning" appearance="soft" size="sm" onClick={report.clearFilters}>
            {titles[fields.length + 1]}
          </Button>
          <div className="paged-report-sort">
            <Select label={text.sortBy} fullWidth={false} options={sortOptions} value={report.sortChoice} onChange={(e) => report.setSortChoice(e.target.value)} />
          </div>
          <div className="exportToExcel">
            <Button
              className="report-export-btn"
              variant="primary"
              appearance="soft"
              size="sm"
              icon={<CloudDownload />}
              iconPosition="end"
              aria-label={text.exportLabel(totalCount)}
              title={text.exportLabel(totalCount)}
              disabled={!totalCount || report.exporting}
              onClick={report.exportAll}
            >
              {report.exporting ? text.exporting : text.export}
            </Button>
          </div>
          {report.canBulkDelete && (
            <div className="delete-by-container" style={{ marginInlineStart: "auto" }}>
              <Button
                variant="danger"
                appearance=""
                size="sm"
                rounded
                icon={<Trash2 size={18} />}
                title={text.deleteFiltered}
                aria-label={text.deleteFiltered}
                disabled={!totalCount || deleteState.checking}
                onClick={(e) => {
                  e.stopPropagation();
                  report.requestDelete();
                }}
              />
            </div>
          )}
        </div>
      </div>

      {report.notice && <NoticeBox variant="warning">{report.notice}</NoticeBox>}
      {deleteState.checking && (
        <p className="report-state" role="status">
          {text.checkingRecords}
        </p>
      )}
    </>
  );
};

// State line of the cards area: first load, failure with retry, empty result.
export const PagedStates = ({ report }) => {
  const { text, summary, stores } = report;
  const loadingFirst = summary.loading && !summary.data;
  const failed = !summary.loading && summary.error;
  return (
    <>
      {loadingFirst && (
        <p className="report-state" role="status">
          {text.loading}
        </p>
      )}
      {failed && (
        <div className="report-state" role="alert">
          <p>{text.loadFailed}</p>
          <Button size="sm" variant="primary" appearance="soft" onClick={summary.reload}>
            {text.retry}
          </Button>
        </div>
      )}
      {!loadingFirst && !failed && summary.data && stores.length === 0 && <p className="report-state">{text.empty}</p>}
    </>
  );
};

// The hierarchy of the open store. `children(anchors, groupRows, hierarchy)` renders the table's expand component; it stays mounted through reloads so
// open rows stay open, and starts collapsed again when the filters or the store change. Every month of the period is there before any row is requested.
export const PagedRows = ({ report, children }) => {
  const { text, groups, anchors, groupRows, hierarchy } = report;
  const pagination = hierarchy.leafPagination;
  const leafFooter = pagination && pagination.totalPages > 1 && (
    <Pagination page={pagination.page} pages={pagination.totalPages} total={pagination.total} onPage={report.setPage} labels={text.pagination} disabled={hierarchy.leaf.loading} />
  );
  return (
    <div className="paged-report-rows">
      {groups.loading && !anchors && (
        <p className="report-state" role="status">
          {text.rowsLoading}
        </p>
      )}
      {!groups.loading && groups.error && (
        <div className="report-state" role="alert">
          <p>{text.loadFailed}</p>
          <Button size="sm" variant="primary" appearance="soft" onClick={groups.reload}>
            {text.retry}
          </Button>
        </div>
      )}
      {!groups.loading && !groups.error && anchors && anchors.length === 0 && <p className="report-state">{text.rowsEmpty}</p>}
      {!groups.error && anchors && anchors.length > 0 && <Fragment key={report.hierarchyKey}>{children(anchors, groupRows, { ...hierarchy, leafFooter })}</Fragment>}
    </div>
  );
};

// The confirmation states what the server counted: how many records, of which report, in which period and stores.
const MAX_STORE_NAMES = 3;
export const PagedDeleteDialog = ({ report, kind }) => {
  const { lang } = useContext(Context);
  const { text, deleteState, filters } = report;
  if (!deleteState.ids) return null;
  const { period, stores } = deleteState.preview;
  const labels = translations[lang].filter.month;
  const names = stores.map((store) => store.name).filter(Boolean);
  const description = text.deleteScope({
    count: deleteState.ids.length,
    report: translations[lang].reports.header[REPORT_LABEL_INDEX[kind]],
    from: formatMonth(period.from, labels),
    to: formatMonth(period.to, labels),
    stores: names.slice(0, MAX_STORE_NAMES).join(lang === "ar" ? "، " : ", "),
    more: Math.max(0, names.length - MAX_STORE_NAMES),
    filtered: ["apps", "branches", "orderStatuses", "causes"].some((name) => filters[name].length > 0),
  });
  return <DeleteModal cancel={report.cancelDelete} id={true} handleDelete={report.confirmDelete} error={deleteState.error} description={description} />;
};
